import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AdminSupportNotificationsContextValue {
  totalUnread: number;
  unreadByTicket: Record<number, number>;
  refresh: () => void;
  markTicketRead: (ticketId: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const AdminSupportNotificationsContext = createContext<AdminSupportNotificationsContextValue>({
  totalUnread: 0,
  unreadByTicket: {},
  refresh: () => {},
  markTicketRead: async () => {},
  markAllRead: async () => {},
});

export function AdminSupportNotificationsProvider({ children }: { children: ReactNode }) {
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByTicket, setUnreadByTicket] = useState<Record<number, number>>({});

  const fetchNotifications = useCallback(async () => {
    try {
      const [countRes, byTicketRes] = await Promise.all([
        fetch(`${BASE_URL}/api/admin/support/notifications/unread-count`, { credentials: "include" }),
        fetch(`${BASE_URL}/api/admin/support/notifications/unread-by-ticket`, { credentials: "include" }),
      ]);
      if (countRes.ok) {
        const data = await countRes.json();
        setTotalUnread(data.count ?? 0);
      }
      if (byTicketRes.ok) {
        const data = await byTicketRes.json();
        setUnreadByTicket(data ?? {});
      }
    } catch {
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markTicketRead = useCallback(async (ticketId: number) => {
    try {
      await fetch(`${BASE_URL}/api/admin/support/notifications/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ticketId }),
      });
      setUnreadByTicket((prev) => {
        const next = { ...prev };
        delete next[ticketId];
        return next;
      });
      setTotalUnread((prev) => Math.max(0, prev - (unreadByTicket[ticketId] ?? 0)));
    } catch {
    }
  }, [unreadByTicket]);

  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${BASE_URL}/api/admin/support/notifications/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      setUnreadByTicket({});
      setTotalUnread(0);
    } catch {
    }
  }, []);

  return (
    <AdminSupportNotificationsContext.Provider value={{ totalUnread, unreadByTicket, refresh: fetchNotifications, markTicketRead, markAllRead }}>
      {children}
    </AdminSupportNotificationsContext.Provider>
  );
}

export function useAdminSupportNotifications() {
  return useContext(AdminSupportNotificationsContext);
}
